import React, { useState } from "react"
import {
  Form,
  Input,
  InputNumber,
  Row,
  Col,
  Select,
  Button,
  AutoComplete,
  Checkbox,
  Typography,
  message,
} from "antd"
import styled from "styled-components"

import { usePharmacies } from "../../../../../hooks/usePharmacies"
import { useTelemedicineCategoryContext } from "../../../../../context/TelemedicineCategoryContext"
import { useStates } from "../hooks/useStates"

const { TextArea } = Input

const StyledFormItem = styled(Col)`
  & label {
    text-transform: capitalize;
  }
`

const StyledButtonGroup = styled(Row)`
  width: 100%;
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;

  & button {
    background: #fff;
    color: #171c2f;
  }
`

const HintText = styled(Typography.Text)`
  display: block;
  color: #888;
  font-size: 0.8rem;
  margin-bottom: 16px;
  text-transform: none;
`

const FormItem = ({ span, children, ...props }) => (
  <StyledFormItem span={span || 12}>
    <Form.Item {...props} label={props.label || props.name}>
      {children}
    </Form.Item>
  </StyledFormItem>
)

const CheckboxField = ({ name, label, onChange }) => (
  <Col span={24} style={{ marginLeft: "7px", marginBottom: "12px" }}>
    <Form.Item name={name} valuePropName="checked" style={{ marginBottom: 0 }} onChange={onChange}>
      <Checkbox>
        <Typography.Text>{label}</Typography.Text>
      </Checkbox>
    </Form.Item>
  </Col>
)

const buildUpdates = ({ values, selectedStates, getStateObjects, unsetStates, selectedPharmacy, showQRD }) => {
  const updates = {}

  if (values.pseudonyms?.length) updates.pseudonyms = values.pseudonyms
  if (values.teleMedicineCategories?.length) {
    updates.teleMedicineCategoryIds = values.teleMedicineCategories
  }
  if (unsetStates) updates.states = []
  else if (selectedStates?.length) updates.states = getStateObjects()
  if (selectedPharmacy) {
    updates.pharmacyId = selectedPharmacy.pharmacyId
    updates.pharmacyName = selectedPharmacy.name
  }
  // null means "no change" (allowClear cleared); true/false are explicit selections
  if (values.DAW != null) updates.DAW = values.DAW
  if (showQRD) {
    if (values.pillCount != null) updates.pillCount = values.pillCount
    if (values.refills != null) updates.refills = values.refills
    if (values.daysSupply != null) updates.daysSupply = values.daysSupply
  }
  if (values.directions?.trim()) updates.directions = values.directions
  if (values.pharmacyNotes?.trim()) updates.pharmacyNotes = values.pharmacyNotes

  return updates
}

export const BulkUpdateForm = ({ onSubmit, onCancel, selectedCount }) => {
  const [showQRD, setShowQRD] = useState(false)
  const [unsetStates, setUnsetStates] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [form] = Form.useForm()

  const { telemedicineCategories } = useTelemedicineCategoryContext()

  const {
    pharmacySearchResults,
    pharmaciesLoading,
    selectPharmacyById,
    selectedPharmacy,
    searchPharmacies,
  } = usePharmacies()

  const {
    selectedStates,
    searchMatches,
    selectAll,
    reset: resetStates,
    select,
    deselect,
    search,
    getStateObjects,
  } = useStates()

  const telemedicineCategoryOptions = telemedicineCategories.map(c => ({
    label: c.name,
    value: c._id,
  }))

  const pharmacyOptions = pharmacySearchResults?.map(({ pharmacyId, name, city, state }) => ({
    label: `${name}, ${city} ${state}`,
    value: pharmacyId,
  }))

  const resetForm = () => {
    form.resetFields()
    resetStates()
    setShowQRD(false)
    setUnsetStates(false)
  }

  const handleSubmit = async () => {
    const updates = buildUpdates({
      values: form.getFieldsValue(),
      selectedStates,
      getStateObjects,
      unsetStates,
      selectedPharmacy,
      showQRD,
    })

    if (!Object.keys(updates).length) {
      return message.warning("No fields were filled in. Please update at least one field.")
    }

    setSubmitting(true)
    try {
      await onSubmit(updates)
      resetForm()
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Form form={form} layout="vertical" style={{ textTransform: "capitalize" }}>
      <HintText>
        Only filled fields will be applied. Empty fields are left unchanged on each selected
        template.
      </HintText>
      <Row gutter={16}>
        <FormItem span={24} label="Replace Pseudonyms / NDCs" name="pseudonyms">
          <Select
            id="bulk-update-form-pseudonyms-select"
            mode="tags"
            style={{ width: "100%" }}
            placeholder="Replaces all existing NDCs on each selected template"
          />
        </FormItem>
        <FormItem span={24} label="Replace Telemedicine Categories" name="teleMedicineCategories">
          <Select
            id="bulk-update-form-categories-select"
            mode="multiple"
            allowClear
            placeholder="Replaces all existing categories on each selected template"
            options={telemedicineCategoryOptions}
          />
        </FormItem>
        <FormItem span={24} label="States" name="states">
          <div style={{ display: "flex", gap: "10px" }}>
            <Select
              id="bulk-update-form-states-select"
              mode="multiple"
              onSearch={search}
              onSelect={select}
              onDeselect={deselect}
              options={searchMatches}
              value={selectedStates}
              autoClearSearchValue
              placeholder="Select states"
              style={{ flex: 1 }}
              disabled={unsetStates}
            />
            <Button id="bulk-update-form-select-all-states-btn" type="primary" onClick={selectAll} disabled={unsetStates}>All</Button>
            <Button id="bulk-update-form-clear-states-btn" danger disabled={unsetStates || !selectedStates.length} onClick={resetStates}>
              Clear
            </Button>
            <Button
              id="bulk-update-form-unset-states-btn"
              danger
              type={unsetStates ? 'primary' : 'default'}
              onClick={() => {
                setUnsetStates(prev => !prev)
                resetStates()
              }}
            >
              Unset All
            </Button>
          </div>
        </FormItem>
        <FormItem span={24} label="Pharmacy" name="pharmacy">
          <AutoComplete
            id="bulk-update-form-pharmacy-autocomplete"
            loading={pharmaciesLoading}
            defaultActiveFirstOption
            onSearch={searchPharmacies}
            onSelect={selectPharmacyById}
            options={pharmacyOptions}
            placeholder="Enter Pharmacy Name"
          />
        </FormItem>
        <CheckboxField
          name="qrd"
          label="Set Quantity, Refills & Days Supply"
          onChange={e => setShowQRD(e.target.checked)}
        />
        {showQRD && (
          <>
            <FormItem span={8} name="pillCount" label="Quantity">
              <InputNumber style={{ width: '100%' }} placeholder="Enter quantity" min={1} />
            </FormItem>
            <FormItem span={8} name="refills" label="Refills">
              <InputNumber style={{ width: '100%' }} placeholder="Enter refills" min={0} />
            </FormItem>
            <FormItem span={8} label="Days Supply" name="daysSupply">
              <InputNumber style={{ width: '100%' }} placeholder="Enter days supply" min={1} />
            </FormItem>
          </>
        )}
        <FormItem span={24} label="Patient Directions" name="directions">
          <TextArea placeholder="Enter Patient Directions" />
        </FormItem>
        <FormItem span={24} label="Pharmacy Notes" name="pharmacyNotes">
          <TextArea placeholder="Enter Pharmacy Notes" />
        </FormItem>
        <FormItem span={24} label="Dispense as Written (DAW)" name="DAW">
          <Select id="bulk-update-form-daw-select" placeholder="No change" allowClear>
            <Select.Option value={true}>Yes – Dispense as Written</Select.Option>
            <Select.Option value={false}>No – Generic Allowed</Select.Option>
          </Select>
        </FormItem>
        <StyledButtonGroup>
          <Button id="bulk-update-form-cancel-btn" onClick={() => { resetForm(); onCancel() }}>Cancel</Button>
          <Button id="bulk-update-form-submit-btn" style={{ background: "#7BAD7E", color: "#fff" }} loading={submitting} onClick={handleSubmit}>
            Update {selectedCount} Template{selectedCount !== 1 ? "s" : ""}
          </Button>
        </StyledButtonGroup>
      </Row>
    </Form>
  )
}
